import { fetcher } from '@data/utils/api/fetcher/fetcher';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import { useApplications } from '@data/hooks/api/use-applications-api/use-applications-api';
import { userShape, type User } from 'eds-common/api/v3';
import { z } from 'zod';

export interface UseGetStudentsByClassAndEdAppApiParams {
  classId: string;
  edApp: string;
  /**
   * The product codes to ask for. Defaults to the signed-in user's own
   * entitlements, which is the wrong question for an admin reading another
   * educator's class: an admin need not hold the product being reported on,
   * and with no codes the roster is never asked for at all.
   */
  productCodes?: string[] | undefined;
}

async function getStudentsByClassAndEdApp({
  classId,
  productCodes,
}: {
  classId: string;
  productCodes: string[];
}): Promise<User[]> {
  const students = await fetcher<User[]>(
    `/users/classes/${classId}/user-types/STUDENT`,
    {
      service: '/roster',
      version: '/v3',
      queryParameters: { productCodes },
    }
  );

  return z.array(userShape).parse(students);
}

/** Query options for use with `queryClient.ensureQueryData` in route loaders. */
export function studentsByClassAndEdAppQuery({
  classId,
  edApp,
  productCodes,
}: UseGetStudentsByClassAndEdAppApiParams & { productCodes: string[] }) {
  return {
    queryKey: ['students-by-class-and-ed-app', classId, edApp, productCodes],
    queryFn: () => getStudentsByClassAndEdApp({ classId, productCodes }),
  };
}

export function useGetStudentsByClassAndEdAppApi(
  {
    classId,
    edApp,
    productCodes: givenProductCodes,
  }: UseGetStudentsByClassAndEdAppApiParams,
  customQueryOptions?: Partial<UseQueryOptions<User[]>>
) {
  const { data: applications } = useApplications();

  const productCodes =
    givenProductCodes ??
    applications
      ?.find((app) => app.applicationCode === edApp)
      ?.products.map((product) => product.productCode) ??
    [];

  const { enabled = true } = customQueryOptions ?? {};

  return useQuery<User[]>({
    ...studentsByClassAndEdAppQuery({ classId, edApp, productCodes }),
    ...customQueryOptions,
    // Last, so a caller's own `enabled` narrows this guard rather than
    // replacing it: a request with no ids is never worth sending.
    enabled: enabled && Boolean(classId && edApp) && productCodes.length > 0,
  });
}
